import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { adminAPI } from '../../services/api';
import Panel from '../crt/Panel';
import SeverityChip from './SeverityChip';

const fmtNum = (n: number) => (n ?? 0).toLocaleString();

const OverviewTab: React.FC = () => {
  const { data: overview } = useQuery({
    queryKey: ['admin-overview'],
    queryFn: adminAPI.getOverview,
    staleTime: 30000,
    refetchInterval: 30000,
  });

  const jobs = overview?.jobs;
  const runs24h = overview?.runs_24h || {};
  const schemas = overview?.schemas || [];
  const audit = overview?.audit;

  return (
    <Grid container spacing={3}>
      {/* Job health */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Panel label="ETL JOB HEALTH">
          {jobs ? (
            <Grid container spacing={2}>
              <Grid size={4}>
                <Typography variant="caption" color="textSecondary">ENABLED</Typography>
                <Typography variant="h4" fontWeight={700}>{fmtNum(jobs.enabled_count)}</Typography>
              </Grid>
              <Grid size={4}>
                <Typography variant="caption" color="textSecondary">FAILING</Typography>
                <Typography variant="h4" fontWeight={700} color={jobs.failing_count > 0 ? 'error' : 'textPrimary'}>
                  {fmtNum(jobs.failing_count)}
                </Typography>
              </Grid>
              <Grid size={4}>
                <Typography variant="caption" color="textSecondary">DISABLED</Typography>
                <Typography variant="h4" fontWeight={700}>{fmtNum(jobs.disabled_count)}</Typography>
              </Grid>
              <Grid size={6}>
                <Typography variant="caption" color="textSecondary">TOTAL RUNS</Typography>
                <Typography variant="h5" fontWeight={600}>{fmtNum(jobs.total_runs)}</Typography>
              </Grid>
              <Grid size={6}>
                <Typography variant="caption" color="textSecondary">CUMULATIVE ERRORS</Typography>
                <Typography variant="h5" fontWeight={600}>{fmtNum(jobs.total_errors)}</Typography>
              </Grid>
            </Grid>
          ) : (
            <Typography color="textSecondary">Loading...</Typography>
          )}
        </Panel>
      </Grid>

      {/* Last 24h runs */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Panel label="LAST 24 HOURS">
          {Object.keys(runs24h).length > 0 ? (
            <Grid container spacing={2}>
              {['success', 'failed', 'running', 'pending'].map((s) => (
                <Grid size={3} key={s}>
                  <Typography variant="caption" color="textSecondary">{s.toUpperCase()}</Typography>
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    color={s === 'failed' && (runs24h[s] ?? 0) > 0 ? 'error' : 'textPrimary'}
                  >
                    {fmtNum(runs24h[s] || 0)}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography color="textSecondary">No runs in last 24h</Typography>
          )}
        </Panel>
      </Grid>

      {/* Audit summary */}
      <Grid size={12}>
        <Panel label="DB HEALTH AUDIT">
          {audit?.counts ? (
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
              {(['CRITICAL', 'MAJOR', 'MINOR', 'OK'] as const).map((sev) => (
                <Box key={sev} display="flex" gap={1} alignItems="center">
                  <SeverityChip severity={sev} />
                  <Typography variant="h5" fontWeight={700}>
                    {fmtNum(audit.counts[sev] || 0)}
                  </Typography>
                </Box>
              ))}
              <Typography variant="caption" color="textSecondary" sx={{ ml: 'auto' }}>
                {audit.age_seconds != null ? `cached ${audit.age_seconds}s ago` : 'not yet run'} — see Audit tab to refresh
              </Typography>
            </Box>
          ) : (
            <Typography color="textSecondary">
              Audit not yet run — open the Audit tab to generate the first report.
            </Typography>
          )}
        </Panel>
      </Grid>

      {/* Schema row counts */}
      <Grid size={12}>
        <Panel label="SCHEMA ROW TOTALS">
          <Grid container spacing={1}>
            {schemas.map((s: any) => (
              <Grid size={{ xs: 6, sm: 4, md: 3 }} key={s.schema}>
                <Box
                  sx={{
                    p: 1.5,
                    border: '1px solid var(--crt-border)',
                  }}
                >
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                    {s.schema.toUpperCase()}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {fmtNum(s.row_total)} rows / {s.table_count} tables
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Panel>
      </Grid>
    </Grid>
  );
};

export default OverviewTab;
